<template>
    <div class="wave-charts">
        <div class="chart-row">
            <div class="chart-item" ref="uChartContainer"></div>
            <div class="chart-item" ref="iChartContainer"></div>
        </div>

        <!-- 如果没有数据，显示提示 -->
        <div v-if="!uData.length && !iData.length" class="no-data-tip">
            暂无数据，请刷新重试
        </div>
    </div>
</template>

<script>
import * as echarts from 'echarts';

export default {
    name: 'WaveCharts',
    props: {
        uData: { type: Array, default: () => [] },
        iData: { type: Array, default: () => [] }
    },
    data() {
        return {
            charts: [], // 存储图表实例
            xAxisData: Array.from({ length: 960 }, (_, i) => i + 1),// X轴数据, 一共是15*64=960个点
            resizeTimer: null // 用于防抖
        };
    },
    mounted() {
        window.addEventListener('resize', this.handleResize);
        // 页面加载时尝试初始化（防止数据是同步传入的）
        this.$nextTick(() => {
            this.checkAndInitCharts();
        });
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
        this.charts.forEach(chart => chart && chart.dispose());
        this.charts = [];
    },
    watch: {
        // 监听数据变化，数据回来后再渲染
        uData: {
            handler() { this.checkAndInitCharts(); },
            deep: true
        },
        iData: {
            handler() { this.checkAndInitCharts(); },
            deep: true
        }
    },
    methods: {
        // 检查并初始化
        checkAndInitCharts() {
            // 等待 DOM 更新
            this.$nextTick(() => {
                // 只有当有数据时才处理
                if ((!this.uData.length && !this.iData.length)) return;

                // 1. 处理电流图表
                const iEl = this.$refs.iChartContainer;
                if (iEl && this.iData.length > 0) {
                    this.renderChart('iChart', iEl, this.iData, 0);
                }

                // 2. 处理电压图表
                const uEl = this.$refs.uChartContainer;
                if (uEl && this.uData.length > 0) {
                    this.renderChart('uChart', uEl, this.uData, 1);
                }

                // 3. 连接图表（实现联动）
                if (this.charts.length > 0) {
                    echarts.connect(this.charts);
                }
            });
        },

        // 封装渲染逻辑
        renderChart(type, dom, data, index) {
            // 查找是否已存在该图表实例
            let chartInstance = this.charts.find(c => c && c._type === type);

            if (!chartInstance) {
                // 如果不存在，初始化
                // 安全检查：防止容器高度为0导致报错
                if (dom.clientWidth === 0 || dom.clientHeight === 0) {
                    console.warn(`图表容器 ${type} 尺寸异常，请检查 CSS`);
                    return;
                }
                chartInstance = echarts.init(dom);
                chartInstance._type = type; // 标记类型方便查找
                this.charts.push(chartInstance);
            }

            // 设置配置项
            const options = this.creatOption(data, index);
            chartInstance.setOption(options);
        },

        createCurrentOption() { return this.creatOption(this.iData, 0); },
        createVoltageOption() { return this.creatOption(this.uData, 1); },

        creatOption(data, index) {
            const colors = [
                "#9c9c00", "#009c9c", "#9c009c", "#9c5000",
                "#00509c", "#50009c", "#9c9c9c", "#509c00",
                "#9c0000", "#009c00", "#00009c", "#9c5050"
            ];

            return {
                backgroundColor: "transparent", // 建议改为透明以继承背景色
                title: {
                    text: index == 0 ? "电流波形" : "电压波形",
                    left: "center",
                    textStyle: { color: "#373b47", fontSize: 14, fontWeight: "normal" },
                },
                tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
                legend: {
                    data: data.map(item => item.name),
                    top: 'top',
                    right: 'right',
                    padding: [10, 20, 0, 0], // 增加上内边距，让图例和图表内容有点间隙
                },
                axisPointer: { link: [{ xAxisIndex: "all" }] },
                grid: { left: '3%', right: '4%', bottom: '15%', top: '15%', containLabel: true },
                xAxis: [{
                    type: 'category',
                    data: this.xAxisData,
                    axisLabel: { color: colors[index] },
                    axisLine: { lineStyle: { color: "#373b47" } },
                    splitLine: { show: false }
                }],
                yAxis: [{
                    type: "value",
                    axisLabel: { color: "#373b47" },
                    axisLine: { lineStyle: { color: "#373b47" } },
                    splitLine: { show: false }
                }],
                dataZoom: [
                    { type: "inside", start: 0, end: 100 }, 
                    { type: "slider", start: 0, end: 100 }
                ],
                series: data.map((item, index) => ({
                    name: item.name,
                    type: "line",
                    smooth: true,
                    showSymbol: false,
                    data: item.data,
                    color: colors[index]
                }))
            };
        },
        // 处理窗口大小变化
        handleResize() {
            // 防抖处理，避免频繁触发
            if (this.resizeTimer) clearTimeout(this.resizeTimer);
            this.resizeTimer = setTimeout(() => {
                this.charts.forEach(chart => chart && chart.resize());
            }, 300);
        }
    }
}
</script>

<style scoped>
.wave-charts {
    width: 100%;
    gap: 32px;
    padding: 20px;
}

.chart-row {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 50px;
    /* 两个图表之间的间距 */
}

.chart-item {
    height: 350px;
    min-width: 100px;
    /* 防止内容溢出导致 flex 布局失效 */
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    border: 1px solid #e8e8e8;
    box-sizing: border-box;
}
</style>